import React from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    Translate as TranslateIcon,
    WarningAmberRounded as StaleIcon,
    LockOutlined as LockIcon,
} from '@mui/icons-material';
import { t } from '../dashboard/coach/coachTokens';
import useEntityTranslations from '../../hooks/useEntityTranslations';
import type { TranslatableTypeCode } from '../../services/TranslationService';

/**
 * The drop-in entity-translation editor.
 *
 * Renders one input per (field x locale) ENTIRELY from `GET /admin/translation/schema/{type}`.
 * There is no locale code, no field name and no locale count anywhere in this file — enabling a
 * 10th language on the server grows the panel on its own, which is the acceptance criterion for
 * the whole N-locale refactor. `fieldLabels` is the one optional per-host nicety; a field with no
 * label still renders, under its server key.
 *
 * ## What this panel does NOT edit
 *
 * The canonical locale (Hebrew) is shown read-only, as the text to translate FROM. It is edited
 * through the host form's own field, which writes the entity's derived column — the server refuses
 * a canonical write here because that value is the terminus of every fallback chain and is
 * mirrored into a NOT NULL column. Two editors for one value is how they drift.
 *
 * ## Why it saves itself
 *
 * See the header of `useEntityTranslations` — in short: a new entity has no id to translate
 * against, saving an exercise can FORK it to a new id, and translation writes are a separate
 * authorization check that can refuse while the entity save succeeds. The host form's payload and
 * dirty tracking are untouched; the host bumps `reloadToken` after its own save so the panel
 * re-reads the canonical text and the staleness the server just recomputed.
 *
 * ## Per-locale direction
 *
 * `dir` comes from the schema's `rtlInput`/`rtl` per locale, never from the dashboard's own RTL.
 * An Arabic input and an English input sit side by side inside this Hebrew-RTL admin and must each
 * get their own direction, or one of them types backwards.
 *
 * ## Blank CLEARS
 *
 * Emptying an input and saving soft-deletes that field so the reader falls back through the locale
 * chain instead of rendering an empty label. The helper text says so, because it is not guessable.
 */
export interface TranslationPanelProps {
    /** Registered `TranslatableType`. Compile-checked against the generated path parameter. */
    type: TranslatableTypeCode;
    /** The row being translated. Null/undefined (an unsaved entity) renders the "save first" state. */
    entityId?: number | null;
    /** Bump after the host's own save so the canonical text and staleness are re-read. */
    reloadToken?: number | string;
    /** field key -> Hebrew label. Optional; an unlabelled field falls back to its server key. */
    fieldLabels?: Record<string, string>;
    /** Shown in the "save the entity first" state, e.g. "התרגיל". */
    entityNoun?: string;
    /** Called after a successful write, so the host can refresh anything that shows translated text. */
    onSaved?: () => void;
    /** Mirrors "the panel has unsaved edits", for a host that wants to warn on navigate-away. */
    onDirtyChange?: (dirty: boolean) => void;
    /** Hide the panel's own heading when the host already provides one (e.g. inside a dialog). */
    hideHeader?: boolean;
}

const BTN = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    height: 32,
    px: '12px',
    borderRadius: t.rs,
    fontSize: '12.5px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all .14s',
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
} as const;

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: t.rs,
        backgroundColor: t.white,
        fontSize: '14px',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: t.border },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.green },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.green, borderWidth: '2px' },
    },
    '& .MuiInputLabel-root': { fontSize: '14px', color: t.t2, '&.Mui-focused': { color: t.green } },
};

/** A state box — used for every non-editing outcome so none of them can become a dead spinner. */
const StateBox = ({ children }: { children: React.ReactNode }) => (
    <Box
        sx={{
            border: `1px dashed ${t.border}`,
            borderRadius: t.rs,
            background: t.bg,
            padding: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            direction: 'rtl',
        }}
    >
        {children}
    </Box>
);

const TranslationPanel = ({
    type,
    entityId,
    reloadToken,
    fieldLabels,
    entityNoun = 'הפריט',
    onSaved,
    onDirtyChange,
    hideHeader = false,
}: TranslationPanelProps) => {
    const tr = useEntityTranslations({ type, entityId, reloadToken });

    const dirtyRef = React.useRef(false);
    React.useEffect(() => {
        if (dirtyRef.current !== tr.isDirty) {
            dirtyRef.current = tr.isDirty;
            onDirtyChange?.(tr.isDirty);
        }
    }, [tr.isDirty, onDirtyChange]);

    /**
     * Which locale is on screen. One at a time once there is more than one to choose from.
     *
     * The first version rendered every locale stacked, which was fine at two and became a wall at
     * five (4 target locales x 3 fields = 12 inputs in an 85vh dialog) — a translator working in
     * Arabic had to scroll past English, French and Russian to reach it. The tab strip is driven
     * off the schema's locale list exactly like everything else here, so a sixth language adds a
     * sixth tab and changes no code.
     */
    const [activeLocale, setActiveLocale] = React.useState<string | null>(null);

    const targetCodes = React.useMemo(
        () =>
            tr.locales
                .map((locale) => locale.code)
                .filter((code): code is string => Boolean(code) && code !== tr.canonicalLocale),
        [tr.locales, tr.canonicalLocale],
    );

    // Pick (or re-pick) a tab once the schema arrives, and recover if the selected locale is no
    // longer offered — a locale CAN be disabled server-side between two opens of this panel.
    React.useEffect(() => {
        if (targetCodes.length === 0) {
            setActiveLocale(null);
            return;
        }
        setActiveLocale((current) =>
            current && targetCodes.includes(current) ? current : targetCodes[0],
        );
    }, [targetCodes]);

    const labelFor = (key: string) => fieldLabels?.[key] || key;

    /**
     * Per-locale state for the tab marker. `dirty` outranks the rest because it is the only one the
     * user can act on right now; `missing` and `stale` are the two halves of the eventual worklist.
     */
    const localeState = (code: string): 'dirty' | 'stale' | 'missing' | 'ok' => {
        if (tr.isLocaleDirty(code)) return 'dirty';
        if (tr.fields.some((field) => tr.showStale(code, field.key as string))) return 'stale';
        if (!tr.hasStored(code)) return 'missing';
        return 'ok';
    };

    const MARKER: Record<string, string> = {
        dirty: t.blue,
        stale: t.amber,
        missing: t.t3,
        ok: t.green,
    };
    const MARKER_TITLE: Record<string, string> = {
        dirty: 'יש שינויים שלא נשמרו',
        stale: 'המקור השתנה — התרגום דורש עדכון',
        missing: 'אין תרגום',
        ok: 'מתורגם',
    };

    const header = hideHeader ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, direction: 'rtl' }}>
            <TranslateIcon sx={{ color: t.t2, fontSize: 20 }} />
            <Typography sx={{ fontWeight: 700, fontSize: '15px', color: t.text }}>תרגומים</Typography>
        </Box>
    );

    if (tr.status === 'no-entity') {
        return (
            <Box data-testid="translation-panel">
                {header}
                <StateBox>
                    <LockIcon sx={{ color: t.t3, fontSize: 18 }} />
                    <Typography sx={{ fontSize: 13, color: t.t2 }}>
                        {`יש לשמור את ${entityNoun} לפני הוספת תרגומים`}
                    </Typography>
                </StateBox>
            </Box>
        );
    }

    if (tr.status === 'loading') {
        return (
            <Box data-testid="translation-panel">
                {header}
                <StateBox>
                    <CircularProgress size={16} sx={{ color: t.green }} />
                    <Typography sx={{ fontSize: 13, color: t.t2 }}>טוען תרגומים...</Typography>
                </StateBox>
            </Box>
        );
    }

    // The persona has no translation endpoint yet (COMPANY_GROUP_ADMIN). Distinct copy from the
    // 403 below on purpose: that one is about the ITEM, this one is about the API — and telling a
    // group admin their own exercise "cannot be translated" would send them hunting for a
    // permission problem that does not exist.
    if (tr.status === 'unsupported-persona') {
        return (
            <Box data-testid="translation-panel">
                {header}
                <StateBox>
                    <LockIcon sx={{ color: t.t3, fontSize: 18 }} />
                    <Typography data-testid="translation-unsupported" sx={{ fontSize: 13, color: t.t2 }}>
                        {tr.errorMessage || 'ניהול תרגומים אינו זמין עדיין עבור הספרייה שלך'}
                    </Typography>
                </StateBox>
            </Box>
        );
    }

    // The fail-closed case. On the company path `@authz.canTranslate` refuses TEMPLATE and
    // TEMPLATE_WORKOUT by design (their legacy tables have no safe ownership path) and refuses a
    // row outside the caller's company for real. Both land here: a sentence, not a crash and not a
    // spinner that never resolves.
    if (tr.status === 'forbidden') {
        return (
            <Box data-testid="translation-panel">
                {header}
                <StateBox>
                    <LockIcon sx={{ color: t.t3, fontSize: 18 }} />
                    <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.text }}>
                            לא ניתן לנהל תרגומים לפריט זה
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: t.t2 }}>
                            התרגום של סוג פריט זה מנוהל במקום אחר, או שאין לך הרשאה אליו
                        </Typography>
                    </Box>
                </StateBox>
            </Box>
        );
    }

    if (tr.status === 'error') {
        return (
            <Box data-testid="translation-panel">
                {header}
                <StateBox>
                    <Box sx={{ flex: 1 }}>
                        <Typography sx={{ fontSize: 13, color: t.redText }}>
                            {tr.errorMessage || 'שגיאה בטעינת התרגומים'}
                        </Typography>
                    </Box>
                    <Box
                        component="button"
                        type="button"
                        onClick={() => tr.reload()}
                        sx={{
                            ...BTN,
                            background: t.white,
                            color: t.green,
                            border: `1px solid ${t.green}55`,
                        }}
                    >
                        נסה שוב
                    </Box>
                </StateBox>
            </Box>
        );
    }

    const canonicalLocale = tr.locales.find((locale) => locale.code === tr.canonicalLocale);
    const targetLocales = tr.locales.filter((locale) => locale.code !== tr.canonicalLocale);
    const readOnly = tr.writeForbidden;
    // With one target locale a tab strip is pure noise, so the section renders bare. With more than
    // one, exactly the selected locale is mounted — unmounting the others is safe because the
    // drafts live in the hook, not in the inputs, so edits survive a tab switch and "save all"
    // still sees every dirty locale.
    const useTabs = targetLocales.length > 1;
    const shownLocales = useTabs
        ? targetLocales.filter((locale) => locale.code === activeLocale)
        : targetLocales;

    return (
        <Box data-testid="translation-panel" sx={{ direction: 'rtl' }}>
            {header}

            {/* Canonical: read-only, because it is the host form's field that owns it. */}
            <Box
                sx={{
                    border: `1px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.bg,
                    padding: '12px 14px',
                    mb: 2,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: t.text }}>
                        {`${canonicalLocale?.displayName || tr.canonicalLocale} (מקור)`}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: t.t3 }}>
                        נערך בשדות הטופס עצמו
                    </Typography>
                </Box>
                {tr.fields.map((field) => (
                    <Box
                        key={field.key}
                        sx={{ display: 'flex', gap: 1, alignItems: 'baseline', py: '2px' }}
                    >
                        <Typography
                            sx={{ fontSize: 12, color: t.t2, minWidth: 92, flexShrink: 0 }}
                        >
                            {labelFor(field.key as string)}
                        </Typography>
                        <Typography
                            data-testid={`canonical-${field.key}`}
                            sx={{
                                fontSize: 13,
                                color: tr.canonical[field.key as string] ? t.text : t.t3,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {tr.canonical[field.key as string] || '—'}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {readOnly && (
                <Alert severity="info" sx={{ mb: 2, fontSize: 12.5 }}>
                    {tr.errorMessage || 'אין לך הרשאה לערוך תרגומים לפריט זה'}
                </Alert>
            )}
            {!readOnly && tr.errorMessage && (
                <Alert severity="error" sx={{ mb: 2, fontSize: 12.5 }}>
                    {tr.errorMessage}
                </Alert>
            )}

            {/* The locale strip — one tab per non-canonical locale in the schema, nothing fixed. */}
            {useTabs && (
                <Tabs
                    value={activeLocale ?? false}
                    onChange={(_, value) => setActiveLocale(value)}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    data-testid="translation-locale-tabs"
                    sx={{
                        minHeight: 38,
                        mb: 1.5,
                        borderBottom: `1px solid ${t.border}`,
                        '& .MuiTab-root': {
                            minHeight: 38,
                            minWidth: 0,
                            px: 1.5,
                            fontSize: 12.5,
                            fontWeight: 600,
                            fontFamily: 'inherit',
                            color: t.t2,
                            textTransform: 'none',
                            '&.Mui-selected': { color: t.greenText },
                        },
                        '& .MuiTabs-indicator': { backgroundColor: t.green, height: 2 },
                    }}
                >
                    {targetLocales.map((locale) => {
                        const code = locale.code as string;
                        const state = localeState(code);
                        return (
                            <Tab
                                key={code}
                                value={code}
                                data-testid={`locale-tab-${code}`}
                                label={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Tooltip title={MARKER_TITLE[state]}>
                                            <Box
                                                data-testid={`locale-tab-marker-${code}`}
                                                data-state={state}
                                                sx={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: '50%',
                                                    background: MARKER[state],
                                                    flexShrink: 0,
                                                }}
                                            />
                                        </Tooltip>
                                        {locale.displayName || code}
                                    </Box>
                                }
                            />
                        );
                    })}
                </Tabs>
            )}

            {/* The selected locale's fields, straight from the schema. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {shownLocales.map((locale) => {
                    const code = locale.code as string;
                    // Direction per locale from the registry, never from the dashboard's own RTL.
                    const dir = (locale.rtlInput ?? locale.rtl) ? 'rtl' : 'ltr';
                    const busy = tr.savingLocale === code;
                    const dirty = tr.isLocaleDirty(code);
                    const localeStale = tr.fields.some((field) =>
                        tr.showStale(code, field.key as string),
                    );
                    return (
                        <Box
                            key={code}
                            data-testid={`translation-locale-${code}`}
                            sx={{
                                border: `1px solid ${t.border}`,
                                borderRadius: t.rs,
                                background: t.white,
                                overflow: 'hidden',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    padding: '10px 14px',
                                    borderBottom: `1px solid ${t.borderLight}`,
                                    background: t.bg,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: t.text }}>
                                        {locale.displayName || code}
                                    </Typography>
                                    <Chip
                                        label={code}
                                        size="small"
                                        sx={{
                                            height: 18,
                                            fontSize: 10.5,
                                            fontWeight: 600,
                                            color: t.t2,
                                            background: t.borderLight,
                                        }}
                                    />
                                    {localeStale && (
                                        <Tooltip title="הטקסט המקורי השתנה מאז שהתרגום נשמר">
                                            <Chip
                                                icon={<StaleIcon sx={{ fontSize: '13px !important' }} />}
                                                label="דורש עדכון"
                                                size="small"
                                                data-testid={`stale-locale-${code}`}
                                                sx={{
                                                    height: 20,
                                                    fontSize: 10.5,
                                                    fontWeight: 700,
                                                    color: t.amberText,
                                                    background: t.amberLight,
                                                    border: `1px solid ${t.amber}55`,
                                                }}
                                            />
                                        </Tooltip>
                                    )}
                                </Box>

                                {!readOnly && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {tr.hasStored(code) && (
                                            <Box
                                                component="button"
                                                type="button"
                                                disabled={busy}
                                                onClick={async () => {
                                                    if (await tr.clearLocale(code)) onSaved?.();
                                                }}
                                                sx={{
                                                    ...BTN,
                                                    background: t.white,
                                                    color: t.red,
                                                    border: `1px solid ${t.red}33`,
                                                }}
                                            >
                                                {`נקה ${locale.displayName || code}`}
                                            </Box>
                                        )}
                                        <Box
                                            component="button"
                                            type="button"
                                            disabled={busy || !dirty}
                                            onClick={async () => {
                                                if (await tr.saveLocale(code)) onSaved?.();
                                            }}
                                            sx={{
                                                ...BTN,
                                                background: t.green,
                                                color: '#fff',
                                                border: 'none',
                                                '&:hover': { background: t.greenDark },
                                                '&:disabled': {
                                                    background: t.border,
                                                    color: t.t3,
                                                    cursor: 'not-allowed',
                                                },
                                            }}
                                        >
                                            {busy ? (
                                                <CircularProgress size={14} sx={{ color: '#fff' }} />
                                            ) : (
                                                `שמור ${locale.displayName || code}`
                                            )}
                                        </Box>
                                    </Box>
                                )}
                            </Box>

                            <Box
                                sx={{
                                    padding: '14px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.75,
                                }}
                            >
                                {tr.fields.map((field) => {
                                    const key = field.key as string;
                                    const stale = tr.showStale(code, key);
                                    return (
                                        <TextField
                                            key={key}
                                            fullWidth
                                            dir={dir}
                                            label={labelFor(key)}
                                            value={tr.drafts[code]?.[key] ?? ''}
                                            onChange={(e) => tr.setDraft(code, key, e.target.value)}
                                            disabled={readOnly || busy}
                                            multiline={Boolean(field.richText)}
                                            minRows={field.richText ? 4 : undefined}
                                            inputProps={{
                                                dir,
                                                'aria-label': `${labelFor(key)} ${locale.displayName || code}`,
                                            }}
                                            // Only says something when there IS something to say.
                                            // The "blank clears it" note used to sit under every
                                            // field on every locale — 15 copies of a rule you learn
                                            // once — which buried the stale warning, the one line
                                            // here that is actually per-field and actionable.
                                            helperText={
                                                stale
                                                    ? 'המקור השתנה — יש לעדכן את התרגום'
                                                    : field.richText
                                                      ? 'טקסט עשיר (HTML)'
                                                      : undefined
                                            }
                                            FormHelperTextProps={{
                                                sx: {
                                                    fontSize: 11,
                                                    color: stale ? t.amberText : t.t3,
                                                    marginInlineStart: 0,
                                                },
                                            }}
                                            sx={fieldSx}
                                        />
                                    );
                                })}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {!readOnly && useTabs && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                        gap: 1.5,
                        mt: 2,
                    }}
                >
                    <Box
                        component="button"
                        type="button"
                        disabled={!tr.isDirty || Boolean(tr.savingLocale)}
                        onClick={async () => {
                            if (await tr.saveAll()) onSaved?.();
                        }}
                        sx={{
                            ...BTN,
                            height: 36,
                            px: '20px',
                            fontSize: '13.5px',
                            background: t.green,
                            color: '#fff',
                            border: 'none',
                            '&:hover': { background: t.greenDark },
                            '&:disabled': {
                                background: t.border,
                                color: t.t3,
                                cursor: 'not-allowed',
                            },
                        }}
                    >
                        שמור את כל התרגומים
                    </Box>
                    {/* Names the locales this would write, because the tabs holding them are not
                        on screen — otherwise "all" is a promise the user cannot check. */}
                    {tr.isDirty && (
                        <Typography
                            data-testid="translation-dirty-summary"
                            sx={{ fontSize: 12, color: t.t2 }}
                        >
                            {`ממתינים לשמירה: ${tr.dirtyLocales
                                .map(
                                    (code) =>
                                        tr.locales.find((locale) => locale.code === code)
                                            ?.displayName || code,
                                )
                                .join(', ')}`}
                        </Typography>
                    )}
                </Box>
            )}

            {tr.savedLocale && !tr.isDirty && !tr.errorMessage && (
                <Typography
                    data-testid="translation-saved"
                    sx={{ fontSize: 12, color: t.greenText, mt: 1.25, fontWeight: 600 }}
                >
                    התרגומים נשמרו
                </Typography>
            )}
        </Box>
    );
};

export default TranslationPanel;
